import { PDFDocument, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { PdfTheme } from "./pdf-theme";
import { SigningMetadata } from "./signing-metadata";

export interface IntroPageOptions {
  isComplete?: boolean;
  documentTitle?: string;
  existingMetadata?: SigningMetadata;
  theme?: PdfTheme;
  primaryColor?: { r: number; g: number; b: number };
}

/**
 * Generate a consistent, instruction-heavy intro page for the document.
 * Supports a background template _intro.pdf if present.
 */
export async function generateIntroPage(
  pdfDoc: PDFDocument,
  options: IntroPageOptions = {},
): Promise<void> {
  const {
    isComplete = false,
    documentTitle = "Untitled Document",
    existingMetadata,
    theme,
    primaryColor = { r: 0, g: 0.4, b: 0.8 },
  } = options;

  const documentHash = existingMetadata?.documentHash || "N/A";
  const createdAt = existingMetadata?.createdAt
    ? new Date(existingMetadata.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";

  // The creator is assumed to be the first signer in our current logic
  const creator = existingMetadata?.signers[0];
  const creatorKeyId = existingMetadata?.creatorSignature?.keyId;

  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Use custom fonts if theme is provided, fallback to standard
  const fontRegular = theme?.customFont || helvetica;
  const fontBold = theme?.customFontBold || helveticaBold;

  let introPage: PDFPage;
  try {
    // Attempt to load background template for branding
    const introPdfBytes = await fetch("/_intro.pdf").then((res) =>
      res.arrayBuffer(),
    );
    const introPdfDoc = await PDFDocument.load(introPdfBytes);
    const [templatePage] = await pdfDoc.copyPages(introPdfDoc, [0]);
    introPage = pdfDoc.insertPage(0, templatePage);
  } catch (err) {
    // Fallback: Create a clean page with default branding (Letter/A4-ish)
    introPage = pdfDoc.insertPage(0, [595, 842]);
  }

  const pColor = rgb(primaryColor.r, primaryColor.g, primaryColor.b);
  const darkGray = rgb(0.2, 0.2, 0.2);
  const midGray = rgb(0.4, 0.4, 0.4);
  const blackColor = rgb(0, 0, 0);
  const lightGray = rgb(0.6, 0.6, 0.6);

  // --- Header ---
  const mainTitle = isComplete ? "Certified Record" : "Signature Request";
  const tagLine = isComplete
    ? "Cryptographically Bound & Verified."
    : "Sovereign. Secure. Sealed.";

  introPage.drawText("Signed+", {
    x: 50,
    y: 780,
    size: 28,
    font: fontBold,
    color: pColor,
  });
  introPage.drawText(mainTitle, {
    x: 50,
    y: 745,
    size: 24,
    font: fontBold,
    color: darkGray,
  });
  introPage.drawText(tagLine, {
    x: 50,
    y: 720,
    size: 14,
    font: fontRegular,
    color: midGray,
  });

  const createData = [];
  if (creator) {
    const creatorName = creator.name
      ? `${creator.name} (${creator.email})`
      : creator.email;
    createData.push(`Envelope Created By: ${creatorName}`);
  }
  createData.push(`Envelope Created On: ${createdAt}`);

  // --- Body Content ---
  const yStart = 680;
  const lineSpacing = 16;
  let y = yStart;

  const bodyText = isComplete
    ? [
        "Execution Complete",
        "This document has been fully executed and cryptographically sealed using Signed+,",
        "the world's first zero-storage signing platform.",
        "",
        "• Verification: The signatures embedded within are cryptographically bound to the content.",
        "  Any unauthorized modification to the original content will invalidate the authentication.",
        "• Zero-Storage Integrity: Signed+ does not host this document. The entire proof of signature",
        "  is contained within this file's metadata and closing audit pages.",
        "• Sovereign Proof: Authentication is backed by participant-owned OpenPGP keys, ensuring",
        "  mathematical certainty of intent without relying on a central authority.",
        "",
        "You can verify the authenticity of this document at any time using the Signed+ validator",
        "or any standard OpenPGP utility.",
      ]
    : [
        "Action Required",
        "You have been requested to review and sign this document via Signed+.",
        // if creator is provided, show creator name
        ...createData,
        "",
        "[LINE]",
        "",
        "",
        "Signed+ is a zero-storage signing platform, built for sovereignty and privacy:",
        "• Privacy First: We've removed the gatekeeper. The document content never leaves your",
        "  browser, we sign the cryptographic intent, not your data.",
        "• Identity Ownership: Every signature is backed by your own keys. If you don't have one,",
        "  you will be guided to generate your own keys during the process.",
        "• Audit Trail: A high-fidelity, permanent Certificate of Signature will be appended to",
        "  this document once all parties have completed their authentication.",
        "",
        "Please proceed to the following pages to review the content and provide your signature.",
        "",
        "Once you are ready to sign, visit signed.plus to begin the process.",
      ];

  bodyText.forEach((line) => {
    const isHeader =
      line === "Execution Complete" || line === "Action Required";
    const fontSize = isHeader ? 12 : 10;
    const font = isHeader ? fontBold : fontRegular;
    const color = isHeader ? darkGray : blackColor;

    if (line === "") {
      y -= lineSpacing / 2;
    } else if (line === "[LINE]") {
      introPage.drawLine({
        start: { x: 50, y },
        end: { x: 545, y },
        thickness: 0.5,
        color: darkGray,
      });
      y -= lineSpacing;
    } else {
      introPage.drawText(line, {
        x: 50,
        y,
        size: fontSize,
        font,
        color,
        lineHeight: 14,
      });
      y -= lineSpacing;
    }
  });

  // --- Indemnity / Footer Wording ---
  const indemnityY = 120;
  const indemnityLines = [
    "Disclaimer: Signed+ is a platform for cryptographic orchestration. This introductory page and the closing audit",
    "pages are generated by Signed+ to provide a permanent record of execution. All interstitial content pages",
    "between these markers are the sole responsibility of the document generator and participating signers.",
  ];

  indemnityLines.forEach((line, i) => {
    introPage.drawText(line, {
      x: 50,
      y: indemnityY - i * 12,
      size: 8,
      font: fontRegular,
      color: lightGray,
    });
  });

  // --- Document ID Footer ---
  const footerY = 50;
  const footerText = `DOCUMENT: ${documentTitle.toUpperCase()}`;
  const footerHash = `HASH: ${documentHash.slice(0, 32).toUpperCase()}`;

  // Draw a subtle line above the footer
  introPage.drawLine({
    start: { x: 50, y: footerY + 20 },
    end: { x: 545, y: footerY + 20 },
    thickness: 0.5,
    color: lightGray,
    opacity: 0.5,
  });

  introPage.drawText(footerText, {
    x: 50,
    y: footerY,
    size: 8,
    font: fontRegular,
    color: midGray,
  });

  introPage.drawText(footerHash, {
    x: 50,
    y: footerY - 10,
    size: 8,
    font: fontRegular,
    color: midGray,
  });
}
